import { useCallback, useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';

import { observeElementResize } from '#/lib/observe-element-resize';

import { LINE_DRAW_SPEED } from '../HomePagePain/animationUtils';
import { usePauseTimelineWhenOffscreen } from '../HomePagePain/usePauseTimelineWhenOffscreen';
import { ACTIVATIONS } from './atoms';

gsap.registerPlugin(DrawSVGPlugin);

/** Seconds to fade the lit lines out before a loop restarts. */
const FADE_OUT = 0.3;
/**
 * Playback rate for the whole timeline. Matches the Rails Architecture
 * diagram — accumulates rather than pulses, so it reads better slower.
 */
const PLAYBACK_SPEED = LINE_DRAW_SPEED * 0.5;

export type BuildComboTimeline = (args: {
  tl: gsap.core.Timeline;
  /**
   * Light the next activation checkmark. Call once per activation as the
   * right card finishes drawing.
   */
  lightActivation: (index: number) => void;
}) => void;

/**
 * Looping GSAP timeline for the Rails + Sombra combo diagram. Rebuilds on
 * meaningful width changes and pauses offscreen.
 */
export function useComboTimeline(build: BuildComboTimeline) {
  const timelineRef = useRef<GSAPTimeline>(null);
  const container = useRef<HTMLDivElement>(null);
  const [litCount, setLitCount] = useState(0);

  const isVisibleRef = usePauseTimelineWhenOffscreen(container, timelineRef);

  const restartAnimation = useCallback(() => {
    timelineRef.current?.kill();

    const q = gsap.utils.selector(container.current);

    const nextLoop = (tl: gsap.core.Timeline) => {
      setLitCount(0);
      tl.restart();
      if (!isVisibleRef.current) tl.pause();
    };

    const tl = gsap.timeline({
      paused: true,
      onComplete: () => nextLoop(tl),
    });

    const lightActivation = (index: number) => {
      setLitCount(Math.min(index + 1, ACTIVATIONS.length));
    };

    tl.set(q('.svg-draw'), { drawSVG: '0%', opacity: 1 });
    setLitCount(0);
    build({ tl, lightActivation });
    tl.to(
      q('.svg-draw'),
      { opacity: 0, duration: FADE_OUT, ease: 'none' },
      '+=0.5',
    ).to({}, { duration: 0.2 });
    tl.timeScale(PLAYBACK_SPEED);

    timelineRef.current = tl;
    if (isVisibleRef.current) tl.play();
  }, [build, isVisibleRef]);

  useEffect(() => {
    if (!container.current) return;

    let lastWidth = container.current.offsetWidth;
    let timeoutId: NodeJS.Timeout;

    const disconnect = observeElementResize(container.current, (entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (Math.abs(width - lastWidth) > 20) {
          lastWidth = width;
          clearTimeout(timeoutId);
          timeoutId = setTimeout(restartAnimation, 200);
        }
      }
    });

    return () => {
      disconnect();
      clearTimeout(timeoutId);
    };
  }, [restartAnimation]);

  useGSAP(
    () => {
      gsap.delayedCall(0.2, restartAnimation);

      return () => {
        timelineRef.current?.kill();
      };
    },
    { scope: container },
  );

  return { container, litCount };
}
